import React, { useEffect, useMemo, useRef } from 'react'
import * as global from '../../data/global';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import * as An from '../../3ds/an/@index.js';
import { Text3D } from "./Text3D";

export function CustomerText3DBuilder({ ...props }) {
    let groupProps = [];
    let text = '';
    if (props.children) {
        text = props.children.toString();
    }
    let group;
    for (let c = 0; c < text.length; c++) {
        let key = `an${c}`;
        let textc = text[c];
        let an = `An${textc}`;
        let ext = "gltf";
        if (textc.charCodeAt() >= 97 && textc.charCodeAt() <= 122) {
            an = `An${textc}${textc}`;
        }
 
        switch (textc) {
            case '%':
                an = `AnPercent`;
                break;
            case '°':
                an = `AnDegree`;
                break;
            case '-':
                an = `AnDash`;
                break;
            case '@':
                an = `AnAt`;
                break;
            case '.':
                an = `AnDot`;
                break;
            case '/':
                an = `AnFSlash`;
                break;
            case ':':
                an = `AnColon`;
                break;
            case ' ':
                an = `AnSpace`;
                break;
            case '(':
            case ')':
            case ',':
            case '_':
            case '#':
            case '&':
            case '+':
            case '\'':
            case '"':
                an = `AnSpace`;
                break;
            default:
                if (textc >= "A" && textc <= "Z") {
                    an = `An${textc}`;
                } else if (textc >= "0" && textc <= "9") {
                    an = `An${textc}`;
                } else if (!(textc >= "a" && textc <= "z")) {
                    // Lowercase: Anaa, Anbb, … set above — do not replace with AnSpace
                    an = `AnSpace`;
                }
                break;
        }
        if (an === "An9") {
            ext = "glb";
        }
        const cachedGeometry = global.gw.geometries[an];
        if (cachedGeometry?.isBufferGeometry || cachedGeometry?.isGeometry) {
            group = <group key={key} an={an} ext={ext} {...props} dispose={null}>
                {an === "AnSpace" || (props.meshProps && props.meshProps.isHidden) ?
                    <mesh
                        name={an}
                        geometry={cachedGeometry}
                        rotation={[Math.PI / 2, 0, Math.PI / 2]}
                        material={new THREE.MeshStandardMaterial({
                            color: "#cccccc",
                            emissive: "#cccccc",
                            side: THREE.FrontSide,
                            toneMapped: false,
                            envMapIntensity: 1,
                            metalness: 0.0,
                            transparent: true,
                            opacity: 0,
                            visible: false,
                        })}
                        binId={props.meshProps.binId}
                        cableId={props.meshProps.cableId}
                        sensorId={props.meshProps.sensorId}
                        isHumiditySensor={props.meshProps.isHumiditySensor}
                    />
                    :
                    <mesh
                        name={an}
                        geometry={cachedGeometry}
                        rotation={[Math.PI / 2, 0, 0]}
                        {...props.meshProps}
                    />
                }
                    
            </group>;
        }
        else {
            group = <Text3D key={key} an={an} ext={ext} {...props} />;
        }
        groupProps.push(group);
    }
    return (
        <>
            {groupProps}
        </>
    );
}

export function CustomText3D(props) {
    const ref = useRef();
    const hitMaterial = useMemo(() => new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
        colorWrite: false,
        side: THREE.DoubleSide,
    }), []);

    useFrame((state) => {
        if (ref.current == null) return;
        global.updateCompass();
        const { camera } = state;
        ref.current.quaternion.copy(camera.quaternion);
        if (ref.current.parent) {
            const parentQuat = new THREE.Quaternion();
            ref.current.parent.updateWorldMatrix(true, false);
            ref.current.parent.getWorldQuaternion(parentQuat);
            ref.current.quaternion.premultiply(parentQuat.invert());
        }
    });

    useEffect(() => {
        if (ref.current) {
            global.centerText3d(ref.current);
        };
    });

    const showLastRead = (e) => {
        e.stopPropagation();
        const point = e.nativeEvent || e;
        global.showCableNameLastRead(props.cableId, props.layoutCableId, point.clientX, point.clientY);
    };

    const label = props.children != null ? String(props.children) : "";
    const hit = Math.max(28, label.length * 14 + 10);

    return (
        <group
            ref={ref}
            {...props}
            dispose={null}
        >
            <CustomerText3DBuilder
                meshProps={props.meshProps}
            >
                {props.children}
            </CustomerText3DBuilder>
            {props.isCableName ? (
                <mesh
                    name="cableNameHit"
                    userData={{ isCableNameHit: true }}
                    onPointerOver={showLastRead}
                    onPointerMove={showLastRead}
                    onPointerOut={(e) => {
                        e.stopPropagation();
                        global.clearCableNameHover();
                    }}
                >
                    <boxGeometry args={[hit, hit * 0.65, hit]} />
                    <primitive object={hitMaterial} attach="material" />
                </mesh>
            ) : null}
        </group>
    )
}
